import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import GoogleIcon from '@/assets/icons/google.svg?react';
import { SignUpForm } from '@/components/forms/auth/SignUpForm';
import { Logo } from '@/components/global/Logo';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';

export const SignUp = ({
  isInternalHostname,
}: {
  isInternalHostname: boolean;
}) => {
  // state
  const [loading, setLoading] = useState<boolean>(false);

  // custom hooks
  const { signIn } = useSignIn();
  const { isLoaded, signUp } = useSignUp();

  // event handlers
  const handleGoogleSignUp = async (): Promise<void> => {
    if (!isLoaded || !signUp) {
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again later',
      });
      return;
    }

    setLoading(true);
    try {
      const activeOrgId = window.localStorage.getItem('active-org-id');

      await signIn?.authenticateWithRedirect({
        strategy: 'oauth_google',
        continueSignUp: true,
        redirectUrl: activeOrgId
          ? `${window.location.origin}/${activeOrgId}/auth/sso-callback`
          : `${window.location.origin}/auth/sso-callback`,
        redirectUrlComplete: activeOrgId
          ? `${window.location.origin}/${activeOrgId}/auth/join-org`
          : '/',
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again',
      });
    } finally {
      setLoading(false);
    }
  };

  const getSignInLink = (): string => {
    const activeOrgId = window.localStorage.getItem('active-org-id');
    return `/${activeOrgId}/auth/sign-in`;
  };

  return (
    <div className="mx-auto flex min-h-full w-full max-w-[400px] flex-col justify-center px-4 max-sm:items-center lg:w-1/2">
      <div className="flex">{isInternalHostname && <Logo />}</div>
      <h2 className="as-strong mb-6 mt-10 text-black">Create your account</h2>
      <SignUpForm />
      <div className="my-4 flex items-center">
        <div className="h-[1px] w-full bg-neutral-200" />
        <p className="as-title-03 as-strong px-2 text-neutral-500">OR</p>
        <div className="h-[1px] w-full bg-neutral-200" />
      </div>
      <Button
        variant="outline"
        className="w-full"
        onClick={handleGoogleSignUp}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <>
            <GoogleIcon className="h-4 w-4" />
            Continue with Google
          </>
        )}
      </Button>

      <p className="as-title-03 mt-4 text-center text-muted-foreground">
        Already have an account?
        <Link to={getSignInLink()} className="as-strong ml-2 text-brand-700">
          Sign in
        </Link>
      </p>
    </div>
  );
};
